import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Bell,
  Package,
  FlaskConical,
  Factory,
  ShoppingCart,
  FileText,
  Users,
  Check,
  X,
} from 'lucide-react';
import { api } from '../api/client';
import { formatDistanceToNow } from 'date-fns';

const ACTION_ICONS: Record<string, any> = {
  create: { icon: Package, color: 'text-amber-600 bg-amber-50 dark:text-green-500 dark:bg-green-900/30' },
  update: { icon: Package, color: 'text-amber-600 bg-amber-50 dark:text-blue-500 dark:bg-blue-900/30' },
  delete: { icon: X, color: 'text-amber-600 bg-amber-50 dark:text-red-500 dark:bg-red-900/30' },
  approve: { icon: Check, color: 'text-amber-600 bg-amber-50 dark:text-emerald-500 dark:bg-emerald-900/30' },
};

const ENTITY_ICONS: Record<string, any> = {
  formula: FlaskConical,
  raw_material: Package,
  production_order: Factory,
  order: ShoppingCart,
  quote: FileText,
  customer: Users,
};

export default function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch recent activity
  const { data: activityData } = useQuery({
    queryKey: ['activity', 'recent'],
    queryFn: () => api.getActivityLogs({ pageSize: 10 }),
    refetchInterval: 60000, // Refetch every minute
  });

  const activities = activityData?.data || [];
  const unreadCount = activities.length > 0 ? Math.min(activities.length, 5) : 0;

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getEntityLink = (entityType: string, entityId: string) => {
    const routes: Record<string, string> = {
      formula: `/app/formulas/${entityId}`,
      raw_material: `/app/raw-materials/${entityId}`,
      production_order: `/app/production/${entityId}`,
      order: `/app/orders/${entityId}`,
      quote: `/app/quotes/${entityId}`,
      customer: `/app/customers/${entityId}`,
    };
    return routes[entityType] || '/app/activity';
  };

  const formatAction = (action: string, entityType: string) => {
    const entityName = entityType.replace('_', ' ');
    switch (action) {
      case 'create':
        return `New ${entityName} created`;
      case 'update':
        return `${entityName.charAt(0).toUpperCase() + entityName.slice(1)} updated`;
      case 'delete':
        return `${entityName.charAt(0).toUpperCase() + entityName.slice(1)} deleted`;
      case 'approve':
        return `${entityName.charAt(0).toUpperCase() + entityName.slice(1)} approved`;
      default:
        return `${action} on ${entityName}`;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center justify-center w-10 h-10 rounded-xl border transition-colors bg-white/80 border-gray-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 shadow-sm dark:bg-sky-900/30 dark:border-sky-700/40 dark:text-gray-300 dark:hover:bg-sky-800/40 dark:hover:text-white dark:shadow-none"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-orange-500 text-[10px] font-bold text-white">
            {unreadCount}
          </span>
        )}
      </motion.button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-80 bg-white/80 dark:bg-sky-900/30 rounded-xl shadow-lg border border-gray-200 dark:border-sky-700/40 overflow-hidden z-50 backdrop-blur-sm"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-sky-700/40">
              <h3 className="font-semibold text-slate-900 dark:text-white">Notifications</h3>
              <Link
                to="/app/activity"
                onClick={() => setIsOpen(false)}
                className="text-xs text-orange-600 hover:text-orange-700 font-medium"
              >
                View all
              </Link>
            </div>

            {/* Activity List */}
            <div className="max-h-96 overflow-y-auto">
              {activities.length > 0 ? (
                activities.slice(0, 8).map((activity: any, index: number) => {
                  const EntityIcon = ENTITY_ICONS[activity.entityType] || Package;
                  const actionConfig = ACTION_ICONS[activity.action] || ACTION_ICONS.update;

                  return (
                    <Link
                      key={activity.id}
                      to={getEntityLink(activity.entityType, activity.entityId)}
                      onClick={() => setIsOpen(false)}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-sky-800/50 transition-colors border-b border-gray-100 dark:border-sky-700/40 last:border-0"
                    >
                      <div className={`flex-shrink-0 p-2 rounded-lg ${actionConfig.color}`}>
                        <EntityIcon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                          {formatAction(activity.action, activity.entityType)}
                        </p>
                        <p className="text-xs text-slate-400 dark:text-gray-400 truncate">
                          {activity.user?.name || 'System'}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-gray-500 mt-0.5">
                          {activity.createdAt
                            ? formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })
                            : ''}
                        </p>
                      </div>
                    </Link>
                  );
                })
              ) : (
                <div className="py-8 text-center">
                  <Bell className="h-8 w-8 text-slate-300 dark:text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-500 dark:text-gray-400">No recent activity</p>
                </div>
              )}
            </div>

            {/* Footer */}
            {activities.length > 0 && (
              <div className="px-4 py-2 bg-slate-50 dark:bg-gray-900 border-t border-gray-200 dark:border-sky-700/40">
                <Link
                  to="/app/activity"
                  onClick={() => setIsOpen(false)}
                  className="block text-center text-sm text-slate-400 dark:text-gray-400 hover:text-orange-600 font-medium"
                >
                  See all activity
                </Link>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
